<template>
  <v-container ref="dashboardContainer" class="dashboard-chat-container pa-0 ma-0" fluid>
    <v-row v-if="loading" justify="center" class="mt-16">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-row>
    <v-row v-else-if="!hasPublishedAgents" justify="center" class="mt-16">
      <v-col cols="12" class="text-center">
        <v-icon size="64" color="primary" class="mb-4">mdi-robot-outline</v-icon>
        <h2 class="text-h5 mb-2">No Published Agents</h2>
        <p class="text-body-1">You need to publish at least one agent to use the chat feature.</p>
        <v-btn color="primary" @click="navigateToAgents" class="mt-4">
          Go to Agents
        </v-btn>
      </v-col>
    </v-row>
    <template v-else>
      <v-row class="ma-0 chat-body-row">
        <!-- Chat History Drawer + Chat Area -->
        <v-col cols="12" class="pa-0 d-flex chat-body-col">
          <!-- Custom sidebar (always expanded, 240px width) -->
          <div class="custom-sidebar">
            <div class="sidebar-header pa-1 pl-3">
              <div class="sidebar-header-actions">
                <v-btn
                  color="primary"
                  @click="startNewChat"
                  variant="text"
                  icon="mdi-note-plus-outline"
                  size="small"
                >
                </v-btn>
                <v-btn
                  color="primary"
                  variant="text"
                  :icon="hideWorkflowChatsInHistory ? 'mdi-filter-minus-outline' : 'mdi-filter-outline'"
                  size="small"
                  @click="toggleHideWorkflowChatsInHistory(!hideWorkflowChatsInHistory)"
                />
              </div>
              <v-tooltip text="Retry failed responses once when no tool ran and no assistant content was produced." location="right">
                <template #activator="{ props }">
                  <div v-bind="props" class="auto-retry-toggle">
                    <v-checkbox
                      :model-value="autoRetryFailedChatResponses"
                      label="Auto Retry"
                      density="compact"
                      hide-details
                      color="primary"
                      @update:model-value="onAutoRetryToggle"
                    />
                  </div>
                </template>
              </v-tooltip>
            </div>
            <div class="history-list">
              <div
                v-for="chat in chatHistory"
                :key="chat.id"
                @click="openChat(chat.id)"
                class="history-item"
                :class="{ 'active': chat.id === currentChatId }"
              >
                <span class="history-item-title" :title="chat.title">
                  {{ chat.title }}
                </span>
                <div class="history-item-actions">
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props: tooltip }">
                      <v-icon
                        v-bind="tooltip"
                        @click.stop="startEditChat($event, chat.id, chat.title)"
                        size="small"
                        color="primary"
                      >
                        mdi-pencil-outline
                      </v-icon>
                    </template>
                    <span>Edit</span>
                  </v-tooltip>
                  <v-tooltip v-if="!isAutomationChat(chat)" location="top">
                    <template v-slot:activator="{ props: tooltip }">
                      <v-icon
                        v-bind="tooltip"
                        @click.stop="handleDeleteClick($event, chat.id)"
                        size="small"
                        color="primary"
                        class="ml-2"
                      >
                        mdi-delete-forever-outline
                      </v-icon>
                    </template>
                    <span>Delete</span>
                  </v-tooltip>
                </div>
              </div>
            </div>
          </div>

          <v-col ref="chatContentArea" class="pa-0 d-flex flex-column overflow-hidden chat-content-area">
            <PublicAgentChat
              v-if="currentChat && currentAgentDetails"
              :key="currentChat.id"
              :agent="currentAgentDetails"
              :session="currentChat"
              :scroll-debug="dashboardScrollDebugEnabled"
              :auto-retry-failed-responses="autoRetryFailedChatResponses"
              :initial-message="pendingInitialMessage || ''"
              @initial-message-consumed="pendingInitialMessage = null"
            >
              <template #agent-selector>
                <div ref="agentSelect" class="agent-select-inline">
                  <v-select
                    v-model="selectedAgentKey"
                    :items="publishedAgentItems"
                    item-title="name"
                    item-value="key"
                    :label="selectedAgentKey ? '' : 'Select Agent'"
                    variant="underlined"
                    density="compact"
                    slim
                    @update:model-value="onAgentChange"
                    :disabled="loading"
                    hide-details
                  >
                    <template v-slot:item="{ props, item }">
                      <v-list-item
                        v-bind="props"
                        :title="item.raw.name"
                        :subtitle="item.raw.isShared ? `${item.raw.description} (shared by @${item.raw.ownerUsername})` : item.raw.description"
                      >
                        <template v-slot:prepend v-if="item.raw.isShared">
                          <v-icon size="small" color="primary">mdi-share-variant</v-icon>
                        </template>
                      </v-list-item>
                    </template>
                    <template v-slot:selection="{ item }">
                      <span v-if="item.raw.isShared" class="mr-2">
                        <v-icon size="small" color="primary">mdi-share-variant</v-icon>
                      </span>
                      <span>{{ item.raw.name }}</span>
                    </template>
                  </v-select>
                </div>
              </template>
            </PublicAgentChat>
          </v-col>
        </v-col>
      </v-row>
    </template>

    <!-- Edit Chat Dialog -->
    <v-dialog v-model="showEditDialog" max-width="400">
      <v-card>
        <v-card-title>Edit Chat Name</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="editingTitle"
            label="Chat Name"
            variant="outlined"
            autofocus
            @keyup.enter="saveEditChat"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelEditChat">Cancel</v-btn>
          <v-btn color="primary" variant="flat" @click="saveEditChat">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <ConfirmationDialog
      v-model="showDeleteConfirmation"
      title="Confirm Delete"
      message="Are you sure you want to delete this chat history? This action cannot be undone."
      :loading="false"
      @confirm="executeDeleteChat"
      @cancel="showDeleteConfirmation = false"
    />
  </v-container>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapGetters, mapActions } from 'vuex';
import { nanoid } from 'nanoid';
import PublicAgentChat from '@/components/PublicAgentChat.vue';
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue';
import Button from '@/components/sub/Button.vue';
import { PublicAgentDetails, PublicChatSession } from '@/types/public';
import { PendingChatAgent } from '@/store/global';
import { UserAgentConfig, PublishedAgentInfo, SharedAgentInfo } from '@/types/agents';
import { isChatEmpty } from '@/utils/messages';
import { debugLog } from '@/utils/debug';
import { scrollTo } from 'vuetify/lib/composables/goto';
import { getScrollParent } from 'vuetify/lib/util/getScrollParent';
import { filterWorkflowChats } from '@/utils/publicChat';
import { getDashboardTabPath } from '@/router/dashboardTabs';

interface PublishedAgentItem {
  // Unique composite identifier: `${ownerUsername ?? ''}::${slug}`. Used as the
  // v-select item-value so an owned agent and a shared agent with the same slug
  // remain distinguishable.
  key: string;
  name: string;
  slug: string;
  description: string;
  publishedInfo?: PublishedAgentInfo;
  hasVoice?: boolean;
  isShared?: boolean;
  ownerUsername?: string;
}

function buildAgentItemKey(ownerUsername: string | undefined | null, slug: string): string {
  return `${ownerUsername ?? ''}::${slug}`;
}

@Options({
  components: {
    PublicAgentChat,
    ConfirmationDialog,
    Button
  },
  computed: {
    ...mapGetters(['username', 'agentConfigs', 'sharedAgents', 'publicChatHistoryByAgent', 'pendingChatAgent', 'pendingChatMessage', 'selectedTab', 'hideWorkflowChatsInHistory', 'autoRetryFailedChatResponses']),
  },
  watch: {
    pendingChatAgent: {
      handler(value: PendingChatAgent | null) {
        if (value) {
          this.applyPendingChatAgent(value);
        }
      },
      immediate: true
    },
    selectedTab(value: string) {
      if (value === 'chat') {
        this.scrollAgentSelectIntoView();
        // Re-fetch chat history for the selected agent to pick up new sessions
        // (e.g., workflow sub-agent chats created while on another tab)
        if (this.selectedAgentKey && this.username) {
          const selectedAgent = this.publishedAgentItems.find(
            (item: any) => item.key === this.selectedAgentKey
          );
          if (selectedAgent) {
            const agentUsername = selectedAgent.isShared && selectedAgent.ownerUsername
              ? selectedAgent.ownerUsername
              : this.username;
            this.fetchPublicChatHistoryForAgent({ agentUsername, agentSlug: selectedAgent.slug });
          }
        }
      }
    },
    publishedAgentItems() {
      if (this.pendingChatAgent) {
        this.applyPendingChatAgent(this.pendingChatAgent);
      }
    }
  },
  methods: {
    ...mapActions(['savePublicChat', 'updatePublicChatTitle', 'removePublicChat', 'fetchPublicChatHistoryForAgent', 'toggleHideWorkflowChatsInHistory', 'toggleAutoRetryFailedChatResponses']),
  }
})
export default class DashboardChat extends Vue {
  // Vuex getters
  username!: string;
  agentConfigs!: Record<string, UserAgentConfig>;
  sharedAgents!: SharedAgentInfo[];
  publicChatHistoryByAgent!: (username: string, slug: string) => PublicChatSession[];
  pendingChatAgent!: PendingChatAgent | null;
  pendingChatMessage!: string | null;
  selectedTab!: string;
  hideWorkflowChatsInHistory!: boolean;
  autoRetryFailedChatResponses!: boolean;

  // Local: initial message handed to the freshly opened chat to auto-send once (then cleared).
  pendingInitialMessage: string | null = null;

  // Vuex actions
  savePublicChat!: (session: PublicChatSession) => void;
  updatePublicChatTitle!: (session: PublicChatSession) => Promise<boolean>;
  removePublicChat!: (chatId: string) => void;
  fetchPublicChatHistoryForAgent!: (params: { agentUsername: string; agentSlug: string }) => Promise<void>;
  toggleHideWorkflowChatsInHistory!: (value: boolean) => void;
  toggleAutoRetryFailedChatResponses!: (value: boolean) => void;

  // Component state
  loading = true;
  selectedAgentKey: string | null = null;  // composite `${ownerUsername ?? ''}::${slug}`
  previousAgentKey: string | null = null;  // Track previous selection
  currentChatId: string | null = null;
  currentChat: PublicChatSession | null = null;
  showDeleteConfirmation = false;
  chatIdToDelete: string | null = null;
  showEditDialog = false;
  editingChatId: string | null = null;
  editingTitle = '';
  nav = true;
  private pendingSelectionPromise: Promise<void> | null = null;
  private scrollAgentSelectPending = false;
  private dashboardScrollDebugEnabled = false;
  private dashboardScrollDebugLastLogAt = 0;
  private readonly dashboardScrollDebugThrottleMs = 120;
  private readonly dashboardScrollDebugStorageKey = 'msq:scroll-debug';
  private dashboardOnResize: ((event: UIEvent) => void) | null = null;
  private dashboardOnContainerScroll: ((event: Event) => void) | null = null;
  private dashboardOnChatAreaScroll: ((event: Event) => void) | null = null;
  private dashboardOnViewportLayoutChange: ((event: Event) => void) | null = null;
  private dashboardViewportRafId: number | null = null;

  onAutoRetryToggle(value: boolean | null): void {
    this.toggleAutoRetryFailedChatResponses(value === true)
  }

  // Computed: Get list of published agents
  get publishedAgents(): UserAgentConfig[] {
    return Object.values(this.agentConfigs).filter((config): config is UserAgentConfig => {
      return config.type === 'user-agent' && 
             config.config.publishedInfo?.isPublished === true;
    });
  }

  get hasPublishedAgents(): boolean {
    return this.publishedAgents.length > 0 || (this.sharedAgents && this.sharedAgents.length > 0);
  }

  // Computed: Transform published agents into dropdown items
  get publishedAgentItems(): PublishedAgentItem[] {
    // Get user's own published agents
    const ownAgents = this.publishedAgents.map((agentConfig) => {
      const config = agentConfig.config;
      const publishedInfo = config.publishedInfo;

      if (!publishedInfo) {
        // This shouldn't happen due to the filter above, but TypeScript needs the guard
        throw new Error('Published info missing for published agent');
      }

      return {
        key: buildAgentItemKey(undefined, publishedInfo.slug),
        name: config.name,
        slug: publishedInfo.slug,
        description: config.description,
        publishedInfo: publishedInfo,
        hasVoice: config.voice?.savedVoiceName ? true : false,
        isShared: false,
        ownerUsername: undefined
      };
    });

    // Get shared agents
    const shared = (this.sharedAgents || []).map((sharedAgent) => ({
      key: buildAgentItemKey(sharedAgent.ownerUsername, sharedAgent.slug),
      name: sharedAgent.agentName,
      slug: sharedAgent.slug,
      description: sharedAgent.description,
      hasVoice: sharedAgent.hasVoice,
      isShared: true,
      ownerUsername: sharedAgent.ownerUsername
    }));

    const allAgents = [...ownAgents, ...shared];
    return allAgents.sort((a, b) => {
      const byName = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      if (byName !== 0) return byName;
      return (a.ownerUsername ?? '').localeCompare(b.ownerUsername ?? '', undefined, { sensitivity: 'base' });
    });
  }

  private findPendingAgentItem(target: PendingChatAgent): PublishedAgentItem | undefined {
    return this.publishedAgentItems.find((item) => {
      if (item.slug !== target.slug) return false;
      if (target.ownerUsername) {
        return item.ownerUsername === target.ownerUsername;
      }
      return true;
    });
  }

  private parseDurationMs(value: string): number | null {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (trimmed.endsWith('ms')) {
      const parsed = Number.parseFloat(trimmed);
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (trimmed.endsWith('s')) {
      const parsed = Number.parseFloat(trimmed);
      return Number.isFinite(parsed) ? parsed * 1000 : null;
    }
    const parsed = Number.parseFloat(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private getVWindowTransitionMs(el: HTMLElement): number {
    if (typeof window === 'undefined') return 0;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return 0;
    }
    const windowRoot = el.closest('.v-window');
    if (!windowRoot) return 300;
    const raw = window.getComputedStyle(windowRoot).getPropertyValue('--v-window-transition-duration');
    return this.parseDurationMs(raw) ?? 300;
  }

  private nextAnimationFrame(): Promise<void> {
    return new Promise((resolve) => window.requestAnimationFrame(() => resolve()));
  }

  private async waitForAnimationFrames(count: number): Promise<void> {
    for (let i = 0; i < count; i += 1) {
      await this.nextAnimationFrame();
    }
  }

  private parseDebugFlag(value: string | null): boolean | null {
    if (value == null) return null;
    const normalized = value.trim().toLowerCase();
    if (normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'on') return true;
    if (normalized === '0' || normalized === 'false' || normalized === 'no' || normalized === 'off') return false;
    return null;
  }

  private resolveDashboardScrollDebugEnabled(): boolean {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    const queryValue = this.parseDebugFlag(params.get('scrollDebug'));
    if (queryValue != null) return queryValue;

    let storageValue: boolean | null = null;
    try {
      storageValue = this.parseDebugFlag(window.localStorage.getItem(this.dashboardScrollDebugStorageKey));
    } catch {
      storageValue = null;
    }
    if (storageValue != null) return storageValue;

    const globalValue = (window as unknown as { __MSQ_SCROLL_DEBUG__?: unknown }).__MSQ_SCROLL_DEBUG__;
    if (typeof globalValue === 'boolean') return globalValue;

    return false;
  }

  private resolveDashboardContainerElement(): HTMLElement | null {
    const ref = this.$refs.dashboardContainer as { $el?: unknown } | HTMLElement | undefined;
    if (ref instanceof HTMLElement) return ref;
    if (ref && typeof ref === 'object' && '$el' in ref) {
      const el = (ref as { $el?: unknown }).$el;
      if (el instanceof HTMLElement) return el;
    }
    if (this.$el instanceof HTMLElement) {
      const el = this.$el.closest('.dashboard-chat-container');
      if (el instanceof HTMLElement) return el;
    }
    return null;
  }

  private resolveChatContentAreaElement(): HTMLElement | null {
    const ref = this.$refs.chatContentArea as { $el?: unknown } | HTMLElement | undefined;
    if (ref instanceof HTMLElement) return ref;
    if (ref && typeof ref === 'object' && '$el' in ref) {
      const el = (ref as { $el?: unknown }).$el;
      if (el instanceof HTMLElement) return el;
    }
    if (this.$el instanceof HTMLElement) {
      const el = this.$el.querySelector('.chat-content-area');
      if (el instanceof HTMLElement) return el;
    }
    return null;
  }

  private scheduleDashboardViewportHeightSync(): void {
    if (typeof window === 'undefined') return;
    if (this.dashboardViewportRafId != null) return;
    this.dashboardViewportRafId = window.requestAnimationFrame(() => {
      this.dashboardViewportRafId = null;
      this.syncDashboardViewportHeight();
    });
  }

  private syncDashboardViewportHeight(): void {
    if (typeof window === 'undefined') return;
    const root = this.resolveDashboardContainerElement();
    if (!root) return;
    const top = Math.max(0, Math.round(root.getBoundingClientRect().top));
    const availableHeight = Math.max(0, window.innerHeight - top);
    root.style.setProperty('--dashboard-available-height', `${availableHeight}px`);
  }

  private getElementMetrics(el: HTMLElement | null): Record<string, unknown> | null {
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const style = window.getComputedStyle(el);
    return {
      tag: el.tagName.toLowerCase(),
      className: typeof el.className === 'string' ? el.className : '',
      rect: {
        top: Math.round(rect.top),
        bottom: Math.round(rect.bottom),
        left: Math.round(rect.left),
        right: Math.round(rect.right),
        width: Math.round(rect.width),
        height: Math.round(rect.height)
      },
      scrollTop: Math.round(el.scrollTop),
      scrollHeight: Math.round(el.scrollHeight),
      clientHeight: Math.round(el.clientHeight),
      clientWidth: Math.round(el.clientWidth),
      overflowY: style.overflowY
    };
  }

  private getScrollContainerMetrics(container: unknown): Record<string, unknown> {
    if (container == null) return { type: 'null' };
    if (container === window) {
      return {
        type: 'window',
        scrollY: Math.round(window.scrollY),
        innerHeight: window.innerHeight
      };
    }
    if (container instanceof HTMLElement) {
      return {
        type: 'element',
        ...this.getElementMetrics(container)
      };
    }
    return { type: typeof container };
  }

  private logDashboardScrollSnapshot(reason: string, extra: Record<string, unknown> = {}, force = false): void {
    if (!this.dashboardScrollDebugEnabled || typeof window === 'undefined') return;
    const now = Date.now();
    if (!force && now - this.dashboardScrollDebugLastLogAt < this.dashboardScrollDebugThrottleMs) return;
    this.dashboardScrollDebugLastLogAt = now;

    const root = this.resolveDashboardContainerElement();
    const chatContentArea = this.resolveChatContentAreaElement();
    const agentSelectRef = this.$refs.agentSelect as { $el?: HTMLElement } | HTMLElement | undefined;
    const agentSelect = agentSelectRef instanceof HTMLElement ? agentSelectRef : agentSelectRef?.$el ?? null;
    const sidebar = root?.querySelector('.custom-sidebar');
    const historyList = root?.querySelector('.history-list');
    const docEl = document.documentElement;

    debugLog('[DASHBOARD_SCROLL_DEBUG]', {
      reason,
      ts: now,
      state: {
        loading: this.loading,
        selectedTab: this.selectedTab,
        hasPublishedAgents: this.hasPublishedAgents,
        selectedAgentKey: this.selectedAgentKey,
        currentChatId: this.currentChatId,
        chatHistoryCount: this.chatHistory.length,
        pending: this.scrollAgentSelectPending
      },
      viewport: {
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        scrollY: Math.round(window.scrollY)
      },
      document: {
        clientHeight: docEl.clientHeight,
        scrollHeight: docEl.scrollHeight
      },
      elements: {
        root: this.getElementMetrics(root),
        chatContentArea: this.getElementMetrics(chatContentArea),
        agentSelect: this.getElementMetrics(agentSelect),
        sidebar: this.getElementMetrics(sidebar instanceof HTMLElement ? sidebar : null),
        historyList: this.getElementMetrics(historyList instanceof HTMLElement ? historyList : null)
      },
      extra
    });
  }

  private scrollAgentSelectIntoView(): void {
    if (this.scrollAgentSelectPending) {
      this.logDashboardScrollSnapshot('scrollAgentSelectIntoView.skip.pending');
      return;
    }
    if (typeof window === 'undefined') return;
    this.scrollAgentSelectPending = true;
    this.logDashboardScrollSnapshot('scrollAgentSelectIntoView.start', {}, true);
    const maxAttempts = 8;
    const attempt = async (count: number): Promise<void> => {
      await this.$nextTick();
      if (this.loading || !this.hasPublishedAgents) {
        this.logDashboardScrollSnapshot('scrollAgentSelectIntoView.abort.loadingOrUnavailable', {
          attempt: count,
          loading: this.loading,
          hasPublishedAgents: this.hasPublishedAgents
        }, true);
        this.scrollAgentSelectPending = false;
        return;
      }
      const ref = this.$refs.agentSelect as { $el?: HTMLElement } | HTMLElement | undefined;
      const el = ref instanceof HTMLElement ? ref : ref?.$el;
      this.logDashboardScrollSnapshot('scrollAgentSelectIntoView.attempt', {
        attempt: count,
        hasElement: !!el,
        isConnected: !!el?.isConnected,
        clientRectCount: el?.getClientRects().length ?? 0
      });
      if (!el || !el.isConnected || el.getClientRects().length === 0) {
        if (count < maxAttempts) {
          window.setTimeout(() => {
            void attempt(count + 1);
          }, 50);
        } else {
          this.logDashboardScrollSnapshot('scrollAgentSelectIntoView.fail.maxAttempts', { attempts: maxAttempts }, true);
          this.scrollAgentSelectPending = false;
        }
        return;
      }

      // Wait for VWindow's scroll restore + transition before scrolling into view.
      await this.waitForAnimationFrames(2);
      const delayMs = this.getVWindowTransitionMs(el);
      this.logDashboardScrollSnapshot('scrollAgentSelectIntoView.waitingTransition', {
        attempt: count,
        delayMs
      });
      window.setTimeout(() => {
        const scrollParent = getScrollParent(el, true);
        const before = this.getScrollContainerMetrics(scrollParent);
        void scrollTo(el, {
          container: scrollParent,
          duration: 0,
          easing: 'instant',
          layout: true
        });
        const after = this.getScrollContainerMetrics(scrollParent);
        this.logDashboardScrollSnapshot('scrollAgentSelectIntoView.applied', {
          attempt: count,
          delayMs,
          scrollParentBefore: before,
          scrollParentAfter: after
        }, true);
        this.scrollAgentSelectPending = false;
      }, delayMs);
    };
    void attempt(0);
  }

  /**
   * Apply the pending chat-agent selection. Concurrent callers share a single
   * in-flight promise so that `mounted` can `await` the watcher's invocation
   * (the watcher fires synchronously during component setup via `immediate: true`,
   * before `mounted`). Without this, `mounted`'s "pick first agent" fallback
   * would race with — and overwrite — the watcher's selection.
   */
  private async applyPendingChatAgent(target: PendingChatAgent): Promise<void> {
    if (this.pendingSelectionPromise) {
      return this.pendingSelectionPromise;
    }
    this.pendingSelectionPromise = this.runApplyPendingChatAgent(target);
    try {
      await this.pendingSelectionPromise;
    } finally {
      this.pendingSelectionPromise = null;
    }
  }

  private async runApplyPendingChatAgent(target: PendingChatAgent): Promise<void> {
    let item = this.findPendingAgentItem(target);
    if (!item) {
      await this.$store.dispatch('fetchSharedAgents');
      item = this.findPendingAgentItem(target);
    }
    if (!item) return;

    this.selectedAgentKey = item.key;
    // Capture any seeded message before opening the chat, so the fresh PublicAgentChat mounts with it.
    const seedMessage = this.pendingChatMessage;
    if (seedMessage) {
      this.pendingInitialMessage = seedMessage;
      this.$store.dispatch('clearPendingChatMessage');
    }
    const chatIdBefore = this.currentChatId;
    await this.onAgentChange(item.key, { preferredChatId: target.chatId });
    // onAgentChange starts a new chat when switching agents; if the agent was already active it may
    // not have, so force a fresh chat to guarantee the seeded message lands in an empty conversation.
    if (seedMessage && this.currentChatId === chatIdBefore) {
      this.startNewChat();
    }
    this.scrollAgentSelectIntoView();
    this.$store.dispatch('clearPendingChatAgent');
  }

  // Computed: Get currently selected agent (full item)
  get selectedAgentItem(): PublishedAgentItem | null {
    if (!this.selectedAgentKey) return null;
    return this.publishedAgentItems.find((item) => item.key === this.selectedAgentKey) ?? null;
  }

  // Computed: Get currently selected agent details
  get currentAgentDetails(): PublicAgentDetails | null {
    const agentItem = this.selectedAgentItem;
    if (!agentItem) return null;

    return {
      name: agentItem.name,
      description: agentItem.description,
      hasVoice: agentItem.hasVoice
    };
  }

  // Computed: Get chat history for currently selected agent
  get rawChatHistory(): PublicChatSession[] {
    const selectedAgent = this.selectedAgentItem;
    if (!selectedAgent || !this.username) {
      return [];
    }

    // For shared agents, use the owner's username; for own agents, use current user's username
    const agentUsername = selectedAgent.isShared && selectedAgent.ownerUsername
      ? selectedAgent.ownerUsername
      : this.username;

    return this.publicChatHistoryByAgent(agentUsername, selectedAgent.slug);
  }

  get chatHistory(): PublicChatSession[] {
    return filterWorkflowChats(this.rawChatHistory, this.hideWorkflowChatsInHistory)
  }

  private findEmptyChat(): PublicChatSession | null {
    return this.chatHistory.find((chat) => isChatEmpty(chat.messages)) ?? null;
  }

  async mounted() {
    this.dashboardScrollDebugEnabled = this.resolveDashboardScrollDebugEnabled();
    debugLog('[DASHBOARD_SCROLL_DEBUG] mode', {
      enabled: this.dashboardScrollDebugEnabled,
      storageKey: this.dashboardScrollDebugStorageKey
    });

    if (this.dashboardScrollDebugEnabled) {
      this.dashboardOnResize = () => {
        this.logDashboardScrollSnapshot('window.resize');
      };
      window.addEventListener('resize', this.dashboardOnResize, { passive: true });

      const root = this.resolveDashboardContainerElement();
      if (root) {
        this.dashboardOnContainerScroll = () => {
          this.logDashboardScrollSnapshot('dashboardContainer.scroll');
        };
        root.addEventListener('scroll', this.dashboardOnContainerScroll, { passive: true });
      }

      const chatArea = this.resolveChatContentAreaElement();
      if (chatArea) {
        this.dashboardOnChatAreaScroll = () => {
          this.logDashboardScrollSnapshot('chatContentArea.scroll');
        };
        chatArea.addEventListener('scroll', this.dashboardOnChatAreaScroll, { passive: true });
      }
    }

    this.dashboardOnViewportLayoutChange = () => {
      this.scheduleDashboardViewportHeightSync();
    };
    window.addEventListener('resize', this.dashboardOnViewportLayoutChange, { passive: true });
    window.addEventListener('scroll', this.dashboardOnViewportLayoutChange, { passive: true });
    this.scheduleDashboardViewportHeightSync();

    debugLog('[DashboardChat] mounted', {
      username: this.username,
      hasPublishedAgents: this.hasPublishedAgents,
      publishedCount: this.publishedAgentItems.length
    });
    this.logDashboardScrollSnapshot('mounted.start', {}, true);

    this.loading = true;

    // Fetch shared agents
    await this.$store.dispatch('fetchSharedAgents');

    // The pendingChatAgent watcher (immediate: true) fires synchronously during
    // component setup, before this hook runs. If it kicked off a selection,
    // wait for it to finish before deciding whether to fall back to "pick first".
    if (this.pendingSelectionPromise) {
      await this.pendingSelectionPromise;
    } else if (this.pendingChatAgent) {
      // Watcher hasn't started yet (rare, but possible) — apply directly.
      await this.applyPendingChatAgent(this.pendingChatAgent);
    }

    // If a pending selection succeeded, the watcher path already set the agent
    // and started a chat. Don't override with "pick first".
    if (this.selectedAgentKey) {
      this.loading = false;
      return;
    }

    // If there are published agents (own or shared), select the first one
    if (this.hasPublishedAgents) {
      const firstAgent = this.publishedAgentItems[0];
      this.selectedAgentKey = firstAgent.key;
      this.previousAgentKey = firstAgent.key;

      // Determine the correct username for the agent
      const agentUsername = firstAgent.isShared && firstAgent.ownerUsername
        ? firstAgent.ownerUsername
        : this.username;

      debugLog('[DashboardChat] Fetching history for:', {
        username: agentUsername,
        slug: firstAgent.slug,
        isShared: firstAgent.isShared
      });

      // Fetch chat history for the selected agent
      await this.fetchPublicChatHistoryForAgent({
        agentUsername: agentUsername,
        agentSlug: firstAgent.slug
      });

      debugLog('[DashboardChat] After fetch, chatHistory length:', this.chatHistory.length);

      // Load initial chat
      this.loadInitialChat();
    }

    this.loading = false;
    this.scrollAgentSelectIntoView();
    this.scheduleDashboardViewportHeightSync();
    this.logDashboardScrollSnapshot('mounted.done', {}, true);
  }

  updated(): void {
    this.scheduleDashboardViewportHeightSync();
  }

  beforeUnmount(): void {
    const root = this.resolveDashboardContainerElement();
    if (root && this.dashboardOnContainerScroll) {
      root.removeEventListener('scroll', this.dashboardOnContainerScroll);
      this.dashboardOnContainerScroll = null;
    }

    const chatArea = this.resolveChatContentAreaElement();
    if (chatArea && this.dashboardOnChatAreaScroll) {
      chatArea.removeEventListener('scroll', this.dashboardOnChatAreaScroll);
      this.dashboardOnChatAreaScroll = null;
    }

    if (this.dashboardOnResize) {
      window.removeEventListener('resize', this.dashboardOnResize);
      this.dashboardOnResize = null;
    }
    if (this.dashboardOnViewportLayoutChange) {
      window.removeEventListener('resize', this.dashboardOnViewportLayoutChange);
      window.removeEventListener('scroll', this.dashboardOnViewportLayoutChange);
      this.dashboardOnViewportLayoutChange = null;
    }
    if (this.dashboardViewportRafId != null) {
      window.cancelAnimationFrame(this.dashboardViewportRafId);
      this.dashboardViewportRafId = null;
    }
  }

  // Handler: When agent selection changes. The v-select emits the composite
  // `${ownerUsername ?? ''}::${slug}` key (item-value="key").
  async onAgentChange(newKey: string | null, options: { preferredChatId?: string } = {}): Promise<void> {
    this.logDashboardScrollSnapshot('onAgentChange.called', {
      newKey,
      previousKey: this.previousAgentKey,
      currentKey: this.selectedAgentKey
    }, true);
    debugLog('[DashboardChat] onAgentChange called', {
      newKey,
      previousKey: this.previousAgentKey,
      currentKey: this.selectedAgentKey,
      username: this.username
    });

    if (!newKey) {
      debugLog('[DashboardChat] onAgentChange: no change, returning');
      return;
    }

    const selectedAgent = this.publishedAgentItems.find((item) => item.key === newKey);
    if (!selectedAgent) return;

    // Compare with previousAgentKey instead of selectedAgentKey because v-model
    // updates selectedAgentKey before this handler fires.
    if (newKey === this.previousAgentKey && !options.preferredChatId) {
      debugLog('[DashboardChat] onAgentChange: no change, returning');
      return;
    }

    if (newKey === this.previousAgentKey && options.preferredChatId) {
      const preferredExistingChat = this.chatHistory.find((chat) => chat.id === options.preferredChatId);
      if (preferredExistingChat) {
        this.openChat(preferredExistingChat.id);
        this.loading = false;
        return;
      }
    }

    this.loading = true;
    this.previousAgentKey = newKey;

    // Determine the correct username for the agent
    const agentUsername = selectedAgent.isShared && selectedAgent.ownerUsername
      ? selectedAgent.ownerUsername
      : this.username;

    debugLog('[DashboardChat] onAgentChange: fetching history', {
      agentUsername,
      slug: selectedAgent.slug,
      isShared: selectedAgent.isShared
    });

    // Fetch chat history for the newly selected agent
    await this.fetchPublicChatHistoryForAgent({
      agentUsername: agentUsername,
      agentSlug: selectedAgent.slug
    });

    debugLog('[DashboardChat] onAgentChange: history fetched, length:', this.chatHistory.length);

    if (options.preferredChatId) {
      const preferredChat = this.chatHistory.find((chat) => chat.id === options.preferredChatId);
      if (preferredChat) {
        this.openChat(preferredChat.id);
      } else {
        this.startNewChat();
      }
    } else {
      // Always start a new chat when switching agents.
      this.startNewChat();
    }

    this.loading = false;
    this.logDashboardScrollSnapshot('onAgentChange.done', {
      newKey,
      chatHistoryCount: this.chatHistory.length
    }, true);
  }

  // Load the most recent chat or create a new one
  loadInitialChat(): void {
    // Always start with a new chat when loading the dashboard
    // (history is still visible in the sidebar for the user to open)
    this.startNewChat();
  }

  // Start a new chat with the currently selected agent
  startNewChat(): void {
    const selectedAgent = this.selectedAgentItem;
    if (!selectedAgent || !this.username) {
      console.error('Cannot start new chat: missing agent or username');
      this.logDashboardScrollSnapshot('startNewChat.abort.missingContext', {
        selectedAgentKey: this.selectedAgentKey,
        username: this.username
      }, true);
      return;
    }
    const emptyChat = this.findEmptyChat();
    if (emptyChat) {
      this.currentChatId = emptyChat.id;
      this.currentChat = JSON.parse(JSON.stringify(emptyChat));
      return;
    }

    // Determine the correct username for the agent
    const agentUsername = selectedAgent.isShared && selectedAgent.ownerUsername
      ? selectedAgent.ownerUsername
      : this.username;

    const newChat: PublicChatSession = {
      id: nanoid(),
      agentUsername: agentUsername,
      agentSlug: selectedAgent.slug,
      origin: 'chat',
      title: 'New Chat',
      createdAt: Date.now(),
      updatedAt: Date.now(),
      messages: [],
    };

    // Don't save empty chats - they'll be saved when the first message is sent
    // this.savePublicChat(newChat);
    this.currentChatId = newChat.id;
    this.currentChat = newChat;
    this.logDashboardScrollSnapshot('startNewChat.created', {
      chatId: newChat.id,
      agentSlug: newChat.agentSlug,
      agentUsername: newChat.agentUsername
    }, true);
  }

  // Open an existing chat
  openChat(id: string): void {
    const chat = this.chatHistory.find((c) => c.id === id);
    if (chat) {
      this.currentChatId = chat.id;
      // Deep clone to avoid mutations
      this.currentChat = JSON.parse(JSON.stringify(chat));
      this.logDashboardScrollSnapshot('openChat.selected', {
        chatId: chat.id,
        title: chat.title,
        messageCount: chat.messages.length
      }, true);
    }
  }

  // Navigate to Agents tab
  navigateToAgents(): void {
    this.$store.dispatch('setTab', 'agents');
    const path = getDashboardTabPath('agents');
    if (this.$route.path !== path) {
      this.$router.push(path);
    }
  }

  // Edit chat handlers
  startEditChat(event: Event, chatId: string, currentTitle: string): void {
    event.stopPropagation();
    this.editingChatId = chatId;
    this.editingTitle = currentTitle;
    this.showEditDialog = true;
  }

  async saveEditChat(event?: Event): Promise<void> {
    if (event) event.stopPropagation();

    if (this.editingChatId && this.editingTitle.trim()) {
      const chat = this.chatHistory.find((c) => c.id === this.editingChatId);
      if (chat) {
        const updatedChat: PublicChatSession = {
          ...chat,
          title: this.editingTitle.trim(),
          updatedAt: Date.now()
        };
        await this.updatePublicChatTitle(updatedChat);
        
        // Update current chat if it's the one being edited
        if (this.currentChatId === this.editingChatId) {
          this.currentChat = JSON.parse(JSON.stringify(updatedChat));
        }
      }
    }
    this.cancelEditChat();
  }

  cancelEditChat(event?: Event): void {
    if (event) event.stopPropagation();
    this.editingChatId = null;
    this.editingTitle = '';
    this.showEditDialog = false;
  }

  // Chats produced by a workflow/factory are tied to that automation, so we don't let them be deleted
  // from here (an automation's chat must stay around). Identified by the session's origin tag.
  isAutomationChat(chat: PublicChatSession): boolean {
    return chat.origin === 'workflow' || chat.origin === 'factory'
  }

  // Delete chat handlers
  handleDeleteClick(event: Event, chatId: string): void {
    event.stopPropagation();
    this.chatIdToDelete = chatId;
    this.showDeleteConfirmation = true;
  }

  executeDeleteChat(): void {
    if (this.chatIdToDelete) {
      this.removePublicChat(this.chatIdToDelete);
      
      // If we deleted the current chat, load a different one
      if (this.currentChatId === this.chatIdToDelete) {
        this.loadInitialChat();
      }
      
      this.chatIdToDelete = null;
    }
    this.showDeleteConfirmation = false;
  }
}
</script>

<style scoped lang="scss">
.dashboard-chat-container.v-container {
  height: var(--dashboard-available-height, 100%);
  max-height: var(--dashboard-available-height, 100%);
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  flex-wrap: nowrap;
  align-items: stretch !important;
  justify-content: flex-start !important;
  flex-wrap: nowrap !important;
  overflow: hidden;
}

.agent-select-inline {
  width: 280px;
  min-width: 200px;
  max-width: 320px;
  margin-left: 8px;
}

.chat-body-row {
  flex: 1 1 auto;
  align-self: stretch;
  width: 100%;
  min-height: 0;
  overflow: hidden;
}

.chat-body-col {
  flex: 1 1 auto;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.custom-sidebar {
  width: 240px;
  min-width: 240px;
  height: 100%;
  max-height: 100%;
  background-color: rgb(var(--v-theme-surface));
  border-right: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sidebar-header {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  display: flex;
  flex-direction: column;
}

.sidebar-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.auto-retry-toggle {
  margin-top: 2px;
  margin-left: -4px;
}

.auto-retry-toggle :deep(.v-selection-control) {
  min-height: 24px;
}

.auto-retry-toggle :deep(.v-label) {
  font-size: 12px;
}

.history-list {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
}

.history-item {
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &:hover {
    background-color: rgba(var(--v-theme-primary), 0.08);
    
    .history-item-actions {
      opacity: 1;
    }
  }
  
  &.active {
    background-color: rgba(var(--v-theme-primary), 0.12);
    border-left: 3px solid rgb(var(--v-theme-primary));
    
    .history-item-title {
      color: rgb(var(--v-theme-primary));
      font-weight: 600;
    }
  }
}

.history-item-title {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.875rem;
  margin-right: 8px;
}

.history-item-actions {
  display: flex;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  flex-shrink: 0;
}

.chat-content-area {
  flex: 1;
  height: 100%;
  min-height: 0;
  min-width: 0;
  // max-height: 78vh;
  padding-bottom: 0 !important;
  overflow: hidden;
}

.chat-content-area :deep(.chat-wrapper) {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  align-items: stretch !important;
  justify-content: flex-start !important;
  flex-wrap: nowrap !important;
}

.chat-content-area :deep(.chat-card) {
  flex: 1 1 auto;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}
</style>
